import { Loader2, Send } from 'lucide-react';
import { Button } from '../../ui/button';

interface ClimbEditorFabProps {
  /** Whether everything the save waits for has been answered. */
  ready: boolean;
  saving: boolean;
  onSave: () => void;
  /**
   * Pressed before its time. The page answers where the work is — a red mark on
   * each field still wanted, what the picture is missing written on it, and the
   * first of them scrolled into view.
   */
  onIncomplete: () => void;
  label: string;
}

/**
 * The one button that puts the climb up, bottom-right of every editor.
 *
 * It is never disabled. A dead button in the corner of a long form is the
 * setter's problem to solve blind — they can see it will not go, and nothing on
 * the screen says why. So it stays pressable and answers: green when the climb
 * is ready, plain when it is not, and pressing it while it is not sends the
 * setter to what is still wanted rather than doing nothing.
 *
 * It used to answer with a dialog listing everything missing, which named the
 * work in the one place the work could not be done: the list had to be read,
 * remembered and dismissed before a single field could be filled. Now the page
 * itself carries the answer — see `onIncomplete` — and this stays a button.
 *
 * The icon is a **send**, not a tick: it puts the climb on the wall for
 * everybody, and a tick reads as "done here".
 *
 * It floats over the page and follows the scroll: the picture, the fields and a
 * circuit's list are more than one screenful on a phone, and the setter must not
 * have to find their way back to the save.
 *
 * It has this corner to itself. It used to step up out of the way of a bar of
 * controls at the foot of the screen; those now stand at the mark they act on,
 * on the picture, so there is nothing down here to make room for.
 */
export function ClimbEditorFab({
  ready,
  saving,
  onSave,
  onIncomplete,
  label,
}: ClimbEditorFabProps) {
  return (
    <Button
      type="button"
      size="lg"
      onClick={() => (ready ? onSave() : onIncomplete())}
      disabled={saving}
      aria-label={label}
      title={label}
      data-testid="climb-editor-save"
      data-ready={ready}
      className={`fixed bottom-6 right-6 z-40 size-14 rounded-full p-0 shadow-lg transition-colors ${
        ready
          ? 'bg-green-600 text-white hover:bg-green-700'
          : 'bg-muted text-muted-foreground hover:bg-muted'
      }`}
    >
      {saving ? (
        <Loader2 className="size-6 animate-spin" aria-hidden />
      ) : (
        <Send className="size-6" aria-hidden />
      )}
    </Button>
  );
}
